<template>
  <div>
    <div
      ref="iconEl"
      @click="showPopover = !showPopover"
      v-test="'wavy-onboarding-icon'"
    >
      <Label
        :finishedTaskAmount="completedTasks.length"
        :totalTaskAmount="tasks.length"
        :tooltip="showPopover ? null : {
        text: $t('wavy_onboarding.trial_tasks.heading'),
        position: 'bottom',
      }"
      />
    </div>
    <BasePopNav
      v-if="showPopover"
      :positionOn="iconEl"
      :heading="$t('wavy_onboarding.trial_tasks.heading_welcome', brandName())"
      @close="showPopover = false"
    >
      <List
        :taskData="tasks"
        :migrationStatus="migrationStatus"
        @select="onTaskSelect"
      />
      <template #footer>
        <Flex :justify="daysRemaining >= 0 ? 'between' : 'end'">
          <Flex
            v-if="daysRemaining >= 0"
            direction="column"
            :gap=".25"
          >
            <BaseHeading size="xs">
              {{ $t('wavy_onboarding.trial_tasks.trial_ends_in') }}
            </BaseHeading>
            <BaseHeading v-test="'wavy-onboarding-trial-ends-in'">
              {{ $t(
                    'onboarding.amount_days',
                    { amount: daysRemaining },
                    daysRemaining
                  ) }}
            </BaseHeading>
          </Flex>
          <BaseButton
            :routerLink="{ name: 'subscription' }"
            @click="showPopover = false"
          >
            {{ $t('wavy_onboarding.trial_tasks.button_upgrade') }}
          </BaseButton>
        </Flex>
      </template>
    </BasePopNav>
    <CopyWavyData
      v-if="showCopyWavyDataModal"
      @close="onMigrationModalClose"
      @success="onTaskSuccess"
    />
    <CloseWavyAccount
      v-if="showCloseWavyAccountModal"
      @close="showCloseWavyAccountModal = false"
      @submit="onTaskSuccess"
    />
    <MigrateWebsite
      v-if="showMigrateWebsiteModal"
      @close="showMigrateWebsiteModal = false"
      @submit="onTaskSuccess"
    />
  </div>
</template>
<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useRouter } from 'vue-router';
import dayjs from '@/dayjs';
import eventBus from '@/event-bus';
import { brandName } from '@/helpers/brand-name';
import { modal } from '@/helpers/ui.js';
import { useCompanyStore } from '@/stores/company';
import Label from '../Label.vue';
import List from './List.vue';
import CloseWavyAccount from './modals/close-wavy-account/index.vue';
import CopyWavyData from './modals/copy-wavy-data/index.vue';
import MigrateWebsite from './modals/migrate-website/index.vue';

const router = useRouter();

const showPopover = ref(false);
const iconEl = ref();

const { company } = useCompanyStore();
const daysRemaining = ref(dayjs(company.trialEndAt).diff(dayjs(), 'day'));

const showCopyWavyDataModal = ref(false);
const showCloseWavyAccountModal = ref(false);
const showMigrateWebsiteModal = ref(false);

const onTaskSelect = (taskName: string) => {
  if (taskName === 'setup_company_details') {
    modal('setup-company-info');
  }

  if (taskName === 'trigger_wavy_migration') {
    showCopyWavyDataModal.value = true;
  }

  if (taskName === 'pick_plan') {
    router.push({ name: 'subscription' });
  }

  if (taskName === 'stop_wavy_subscription') {
    showCloseWavyAccountModal.value = true;
  }

  if (taskName === 'migrate_website') {
    showMigrateWebsiteModal.value = true;
  }

  showPopover.value = false;
};

const { result, refetch } = useQuery(gql`
  query getOnboardingTasks {
    onboardingTasks {
      all {
        claimedAt
        completedAt
        id
        task
      }
    }
    currentUser {
      company {
        wavyMigration {
          status
        }
      }
    }
  }
`);

const tasks = computed(() => result.value?.onboardingTasks.all || []);
const migrationStatus = computed(
  () => result.value?.currentUser?.company.wavyMigration?.status,
);
const completedTasks = computed(() =>
  tasks.value.filter((task: any) => task.completedAt),
);

eventBus.$on('update-task', (taskName: string) => {
  if (taskName === 'setup_company_details') {
    onTaskSuccess();
  }
});

eventBus.$on('show-wavy-tasks', () => {
  showPopover.value = true;
});

const onTaskSuccess = () => {
  refetch();
};

const onMigrationModalClose = () => {
  showCopyWavyDataModal.value = false;
  refetch();
};
</script>
